import { useState, useEffect } from "react";
import {
    FaCopy,
    FaVolumeUp,
    FaDollarSign,
    FaEuroSign,
    FaPoundSign,
    FaYenSign,
    FaRupeeSign,
    FaHashtag,
} from "react-icons/fa";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { useTheme } from "../../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHashtag } from "@fortawesome/free-solid-svg-icons";

export default function NumberToWordsConverter() {
    const [inputValue, setInputValue] = useState("");
    const { darkMode } = useTheme();
    const [outputText, setOutputText] = useState("Please enter a number...");
    const [currentMode, setCurrentMode] = useState("USD");
    const [showNotification, setShowNotification] = useState(false);

    // Number to words conversion function
    const NumToWords = (n) => {
        if (n === 0) return "zero";
        const ones = [
            "",
            "one",
            "two",
            "three",
            "four",
            "five",
            "six",
            "seven",
            "eight",
            "nine",
        ];
        const teens = [
            "ten",
            "eleven",
            "twelve",
            "thirteen",
            "fourteen",
            "fifteen",
            "sixteen",
            "seventeen",
            "eighteen",
            "nineteen",
        ];
        const tens = [
            "",
            "",
            "twenty",
            "thirty",
            "forty",
            "fifty",
            "sixty",
            "seventy",
            "eighty",
            "ninety",
        ];
        const thousands = ["", "thousand", "million", "billion", "trillion"];

        function convertChunk(num) {
            let str = "";
            if (num >= 100) {
                str += ones[Math.floor(num / 100)] + " hundred ";
                num %= 100;
            }
            if (num >= 20) {
                str += tens[Math.floor(num / 10)] + " ";
                num %= 10;
            } else if (num >= 10) {
                str += teens[num - 10] + " ";
                num = 0;
            }
            if (num > 0) str += ones[num] + " ";
            return str.trim();
        }

        let str = "";
        if (n < 0) {
            str += "minus ";
            n = Math.abs(n);
        }

        // Split into whole and decimal parts
        const [whole, decimal] = n.toString().split(".");
        let num = parseInt(whole);
        let chunkIndex = 0;

        while (num > 0) {
            const chunk = num % 1000;
            if (chunk) {
                str =
                    convertChunk(chunk) +
                    " " +
                    thousands[chunkIndex] +
                    " " +
                    str;
            }
            num = Math.floor(num / 1000);
            chunkIndex++;
        }

        str = str.trim();

        // Handle decimal part (cents)
        if (decimal) {
            const decimalStr = decimal.padEnd(2, "0").substring(0, 2);
            const decimalNum = parseInt(decimalStr);

            if (decimalNum > 0) {
                str += " and " + NumToWords(decimalNum) + " cents";
            }
        }

        return str;
    };

    // Convert number to words
    const convertNumber = () => {
        const value = inputValue.trim();
        if (value === "") {
            setOutputText("Please enter a number...");
            return;
        }

        const num = parseFloat(value.replace(/,/g, ""));
        if (isNaN(num)) {
            setOutputText("Invalid input");
            return;
        }

        let words = NumToWords(num);

        if (currentMode !== "number") {
            const currencyNames = {
                USD: "dollars",
                EUR: "euros",
                GBP: "pounds",
                JPY: "yen",
                INR: "rupees",
                AUD: "Australian dollars",
                CAD: "Canadian dollars",
            };

            const currencySymbols = {
                USD: "$",
                EUR: "€",
                GBP: "£",
                JPY: "¥",
                INR: "₹",
                AUD: "A$",
                CAD: "C$",
            };

            const symbol = currencySymbols[currentMode];
            const name = currencyNames[currentMode];

            // Format the number with currency symbol
            const formattedNumber =
                symbol +
                num.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });

            words = `${formattedNumber} (${words} ${name})`;
        }

        setOutputText(words);
    };

    // Copy text to clipboard
    const copyText = () => {
        navigator.clipboard.writeText(outputText);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 2000);
    };

    // Speak text
    const speakText = () => {
        const msg = new SpeechSynthesisUtterance(outputText);
        msg.rate = 0.9;
        msg.pitch = 1;
        window.speechSynthesis.speak(msg);
    };

    // Set conversion mode
    const setMode = (mode) => {
        setCurrentMode(mode);
    };

    // Convert number whenever input or mode changes
    useEffect(() => {
        convertNumber();
    }, [inputValue, currentMode]);

    return (
        <>
            <HeaderSection toolId="numToWords" />

            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faHashtag}
                                className="mr-3"
                            />
                            Number to Words Converter
                        </h1>
                        <p className="opacity-90">
                            Convert numbers to words with comprehensive
                            validation checks
                        </p>
                    </div>
                    {/* Notification */}
                    {showNotification && (
                        <div className="fixed bottom-5 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 shadow-lg dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 animate-fadeInOut z-50">
                            Copied to clipboard!
                        </div>
                    )}

                    {/* Converter Card */}
                    <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 ">
                        {/* Header */}
                       

                        {/* Input */}
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Enter a number..."
                            className="w-full px-4 py-3 mb-4 bg-white dark:text-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all duration-200 placeholder-gray-400 text-gray-700"
                        />

                        {/* Currency Selector */}
                        <div className="flex flex-wrap gap-2 mb-6 justify-center">
                            <button
                                onClick={() => setMode("number")}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                                    currentMode === "number"
                                        ? "bg-green-100 border border-green-200 text-green-800 font-medium"
                                        : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 border border-gray-200"
                                }`}
                            >
                                <FaHashtag className="text-gray-600" /> Normal
                            </button>
                            <button
                                onClick={() => setMode("USD")}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                                    currentMode === "USD"
                                        ? "bg-green-100 border border-green-200 text-green-800 font-medium"
                                        : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 border border-gray-200"
                                }`}
                            >
                                <FaDollarSign className="text-gray-600" /> USD
                            </button>
                            <button
                                onClick={() => setMode("EUR")}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                                    currentMode === "EUR"
                                        ? "bg-green-100 border border-green-200 text-green-800 font-medium"
                                        : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 border border-gray-200"
                                }`}
                            >
                                <FaEuroSign className="text-gray-600" /> EUR
                            </button>
                            <button
                                onClick={() => setMode("GBP")}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                                    currentMode === "GBP"
                                        ? "bg-green-100 border border-green-200 text-green-800 font-medium"
                                        : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 border border-gray-200"
                                }`}
                            >
                                <FaPoundSign className="text-gray-600" /> GBP
                            </button>
                            <button
                                onClick={() => setMode("JPY")}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                                    currentMode === "JPY"
                                        ? "bg-green-100 border border-green-200 text-green-800 font-medium"
                                        : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 border border-gray-200"
                                }`}
                            >
                                <FaYenSign className="text-gray-600" /> JPY
                            </button>
                            <button
                                onClick={() => setMode("INR")}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                                    currentMode === "INR"
                                        ? "bg-green-100 border border-green-200 text-green-800 font-medium"
                                        : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 border border-gray-200"
                                }`}
                            >
                                <FaRupeeSign className="text-gray-600" /> INR
                            </button>
                        </div>

                        {/* Output */}
                        <div className="bg-gray-50 dark:text-white dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg min-h-24 mb-6 border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 text-gray-700">
                            <p className="text-lg leading-relaxed">
                                {outputText}
                            </p>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-4">
                            <button
                                onClick={copyText}
                                className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-all focus:ring-2 focus:ring-green-200 focus:outline-none"
                            >
                                <FaCopy /> Copy
                            </button>
                            <button
                                onClick={speakText}
                                className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 font-medium rounded-lg flex items-center justify-center gap-2 transition-all focus:ring-2 focus:ring-gray-200 focus:outline-none"
                            >
                                <FaVolumeUp /> Speak
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <BottomSection toolId="numToWords" />
        </>
    );
}
